import React, { useState, useEffect } from "react";
import { Card, Button, Row, Col, Typography, Divider, Image, notification, Drawer, message } from "antd";
import metaData from "../data/metaData";
import log from "../utils/log";
import useAuthStore from "../states/useAuth";
import useShop from "../hooks/useShop";
import c from "../utils/common";
import { showErrMsg } from "../utils/error";
import AllianceShopTable from "./ShopTable";
import SectionHeader from "./GetStarted/SectionHeader";
import AddressForm from "./Address/Form";
import Name from "./General/Name";

const { Title, Text } = Typography;

const AllianceShopDrawer = ({ isShow, data = [], onAdd, onDelete, onClose, companyId }) => {
  const { addAllianceShop, deleteAllianceShop } = useShop();
  const [id, setId] = useState();
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);

  // Sort data so that shops matching the companyId appear last
  const sortedData = [...data].sort((a, b) => {
    // If 'a' matches the companyId and 'b' does not, place 'b' first
    if (a.companyId === companyId && b.companyId !== companyId) return 1;
    // If 'b' matches the companyId and 'a' does not, place 'a' first
    if (b.companyId === companyId && a.companyId !== companyId) return -1;
    // Otherwise, sort by companyId as usual
    return a.companyId > b.companyId ? -1 : 1;
  });

  const _addAllianceShop = async () => {
    try {
      setIsLoadingUpdate(true);
      let shop = await addAllianceShop(id, companyId);
      onAdd(shop);
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingUpdate(false);
    }
  };

  const _deleteAllianceShop = async (shopId) => {
    try {
      setIsLoadingUpdate(true);
      let shop = await deleteAllianceShop(shopId, companyId);
      onDelete(shop);
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingUpdate(false);
    }
  };

  return (
    <Drawer title="Alliance Shops" width="auto" onClose={onClose} open={isShow}>
      <Row>
        <Col style={{ marginRight: "1rem" }}>
          <Name
            state={{
              data: id,
              onChange: (value) => setId(value.name),
            }}
            readOnly={false}
            placeholder={"Enter shop ID to add"}
          />
        </Col>
        <Col>
          <Button size="large" type="primary" loading={isLoadingUpdate} onClick={_addAllianceShop}>
            Add
          </Button>
        </Col>
      </Row>
      <AllianceShopTable data={sortedData} loading={isLoadingUpdate} canDelete onDelete={_deleteAllianceShop} companyId={companyId} />
    </Drawer>
  );
};

const CompanyProfile = ({ state = {}, readOnly = true, requestStatus }) => {
  const { data, form, onChange } = state;
  const { user } = useAuthStore();
  const { getAllianceShop } = useShop();

  const [allianceShops, setAllianceShops] = useState([]);
  const [isShowAllianceDrawer, setIsShowAllianceDrawer] = useState(false);
  const [isFetchingAllianceShop, setIsFetchingAllianceShop] = useState(false);

  const getAllianceShopData = async () => {
    try {
      if (!data?.id) {
        //missing companyId
        log.error("Missing companyId");
        return;
      }

      setIsFetchingAllianceShop(true);
      const result = await getAllianceShop(data.id, false, false);
      setAllianceShops(result.map((e, i) => ({ ...e, key: i })));
    } catch (error) {
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingAllianceShop(false);
    }
  };

  const addAllianceShop = (newShop) => {
    setAllianceShops((prevShops) => [{ ...newShop, key: prevShops.length }, ...prevShops]);
  };

  const deleteAllianceShop = (deletedShop) => {
    log.debug(deletedShop);
    setAllianceShops((prevShops) => prevShops.filter((shop) => shop.id !== deletedShop.id));
  };

  useEffect(() => {
    getAllianceShopData();
  }, []);

  return (
    <>
      <AllianceShopDrawer
        isShow={isShowAllianceDrawer}
        data={allianceShops}
        onAdd={addAllianceShop}
        onDelete={deleteAllianceShop}
        onClose={() => setIsShowAllianceDrawer(false)}
        companyId={data?.id}
      />

      <Card title={<SectionHeader title="Company" />} style={{ border: "2px solid var(--ion-color-step-150)" }}>
        {user?.role === "approver" && requestStatus === "approved" && allianceShops.length > 0 ? (
          <>
            <Button type="primary" loading={isFetchingAllianceShop} onClick={() => setIsShowAllianceDrawer(true)}>
              Manage Alliance Shops
            </Button>
            <Divider style={{ background: "var(--ion-color-step-200)", height: "2px" }} />
          </>
        ) : null}

        <Row style={{ width: "100%" }}>
          {readOnly ? (
            <Title level={5}>{data?.name}</Title>
          ) : (
            <Name
              state={{
                data: data?.name,
                form: form,
                rules: [{ required: true, message: "required" }],
                onChange: onChange,
              }}
              readOnly={false}
              maxLength={60}
              showCount
            />
          )}
        </Row>

        <Divider />

        <Title level={5}>Address</Title>
        <AddressForm state={{ data: data?.address, form: form, onChange: onChange }} readOnly={readOnly} />

        <Row style={{ width: "100%" }}>
          <Title level={5}>Business Registration</Title>
        </Row>
        <Image
          width={"12vw"}
          height={"15vw"}
          style={{ objectFit: "contain", padding: "0.5rem", border: "1px solid lightgray" }}
          src={metaData.blobPreURL + "clientUpload-" + data?.br + ".jpeg" + metaData.blobPostURL}
        />
      </Card>
    </>
  );
};

export default CompanyProfile;
